import * as React from "react";
import { Typography, Box, Collapse, Avatar, Chip } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../../../assets/styles/tokens";
import IconButton from "@mui/material/IconButton";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Stack } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../../../../../../features/userSlice";
import { TrendsDataSetQueryIds } from "../../../../../../helpers/funcs/trendsAllQueryIDs";
import { useTrendsMobileOverride } from "../../../../../helpers/hooks/useTrendsMobileOverride";
import {
  setFrMobileDataRedux,
  setFrMobileLoading,
} from "../../../../../../../../../../features/trendsSlice";
import OnlyMobgraphSkel from "../../../../../helpers/components/OnlyMobgraphSkel";
import { getMultiLineYAxisConfig } from "../../../../../helpers/funcs/getMultiLineYAxisConfig";

const SERIES = [
  { id: "qftd_ftd", label: "QFTD/FTD", dataKey: "qftd_ftd", color: "#F59E0B" },
  { id: "ftd_regs", label: "FTD/Regs", dataKey: "ftd_regs", color: "#3B82F6" },
  { id: "out_in", label: "Out/In", dataKey: "out_in", color: "#8B5CF6" },
  { id: "regs_out", label: "Regs/Out", dataKey: "regs_out", color: "#F87171" },
];

const FUNNEL_RATIO_DATA_KEYS = SERIES.map((series) => series.dataKey);

// Custom dot — hollow normally, filled + enlarged when active
const CustomDot = (props: any) => {
  const { cx, cy, activeIndex, index, color, dataKey, onDotClick } = props;
  if (cx === undefined || cy === undefined) return null;
  const isActive = activeIndex === index;
  return (
    <g
      style={{ cursor: "pointer" }}
      onClick={(e) => {
        e.stopPropagation();
        onDotClick(dataKey, index);
      }}
    >
      <circle cx={cx} cy={cy} r={10} fill="transparent" />
      <circle
        cx={cx}
        cy={cy}
        r={isActive ? 5 : 3}
        fill={isActive ? color : "white"}
        stroke={isActive ? "white" : color}
        strokeWidth={1}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload, activeSeries }: any) => {
  if (!active || !payload) return null;
  return (
    <Box
      sx={{
        borderRadius: "8px",
        background: colors.Base.White.$value,
        border: "none",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
        fontSize: typography.Size.Text.xs.$value,
        padding: "8px",
      }}
    >
      {payload.map((item: any) => {
        const isActive = item.dataKey === activeSeries;
        return (
          <Typography
            key={item.dataKey}
            sx={{
              fontSize: typography.Size.Text.xs.$value,
              fontWeight: isActive ? 700 : 400,
              lineHeight: typography.Line_height.text.sm.$value,
              color: isActive ? item.color : colors.GrayLightMode[600].$value,
            }}
          >
            {item.name}: {item.value}%
          </Typography>
        );
      })}
    </Box>
  );
};

const LegendChip = ({
  series,
  activeSeries,
  onClick,
}: {
  series: (typeof SERIES)[number];
  activeSeries: string | null;
  onClick: (dataKey: string) => void;
}) => {
  const isActive = activeSeries === series.dataKey;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onClick(series.dataKey)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.5,
        py: 0.5,
        border: "none",
        borderRadius: spacing.Radius.xs.$value,
        backgroundColor: isActive ? series.color : "transparent",
        color: isActive
          ? colors.Base.White.$value
          : colors.GrayLightMode[600].$value,
        cursor: "pointer",
        fontSize: typography.Size.Text.xxs.$value,
        lineHeight: "10px",
        fontWeight: isActive
          ? typography.Font_weight.Semibold.$value
          : typography.Font_weight.Medium.$value,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: isActive ? colors.Base.White.$value : series.color,
          flexShrink: 0,
        }}
      />
      <Box component="span">{series.label}</Box>
    </Box>
  );
};

// Custom X axis tick — bold when active
const CustomXTick = (props: any) => {
  const { x, y, payload, index, activeIndex, activeNote } = props;
  const [day, month] = payload.value.split(" ");
  const isActiveNote = activeNote === index;
  const isActive = activeIndex === index;
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill={
        isActiveNote || isActive
          ? colors.GrayLightMode[900].$value
          : colors.GrayLightMode[600].$value
      }
      fontSize={typography.Size.Text.xxs.$value}
      fontWeight={
        isActive || isActiveNote
          ? typography.Font_weight.Bold.$value
          : typography.Font_weight.Medium.$value
      }
    >
      <tspan x={x} dy="1em">
        {day}
      </tspan>
      <tspan x={x} dy="1.5em">
        {month}
      </tspan>
    </text>
  );
};

export default function FunnelRatioMultiChart() {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [chartWidth, setChartWidth] = React.useState(0);
  const [activeNote, setActiveNote] = React.useState<number | null>(null);
  const [expandedNotes] = React.useState<Set<number>>(new Set());
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [activeSeries, setActiveSeries] = React.useState<string | null>(null);
  const response = useSelector(selectUserData);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const websiteSharedUids = searchParams.get("workspace");
  const dateType = searchParams.get("dateType") || "click_date";
  const isClickQuery = dateType === "click_date";
  const startDate = searchParams.get("startDateTM");
  const endDate = searchParams.get("endDateTM");
  const timeUnit = searchParams.get("timeUnitFrMobile");
  const trendsMobileData = useSelector(
    (state: any) => state.trends.trendsMobileData,
  );
  const frMobileDataRedux = useSelector(
    (state: any) => state.trends.frMobileDataRedux,
  );
  const activityNotesDataTrendsMobile = useSelector(
    (state: any) => state.trends.activityNotesDataTrendsMobile,
  );
  const loading = useSelector((state: any) => state.trends.frMobileLoading);
  const notesLabelFilter = useSelector(
    (state: any) => state.trends.notesLabelFilter,
  );

  const selectedDimensions = React.useMemo(() => {
    const uidList = websiteSharedUids?.split(",") ?? [];
    return (
      response?.data?.website_permissions?.flatMap((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0];
        if (!uidList.includes(shared_uid)) return [];
        return (siteObj[shared_uid].dimensions ?? []).map(
          (dim: any) => dim.dimension_name,
        );
      }) ?? []
    );
  }, [response, websiteSharedUids]);

  const isOverride = !!searchParams.get("timeUnitFrMobile");

  const sourceData = isOverride
    ? frMobileDataRedux?.query_output
    : trendsMobileData?.query_output;

  const trendsData = React.useMemo(() => {
    return sourceData || [];
  }, [sourceData]);

  const filters = useSelector((state: any) => {
    const f = state.trends.mobileFilters;
    return {
      trafficSource: f?.trafficSource?.value || "all",
      country: f?.country?.value || "all",
      campaign: f?.campaign?.value || "1",
      adGroup: f?.adGroup?.value || "1",
      keyword: f?.keyword?.value || "1",
    };
  });

  const filteredNotes = React.useMemo(() => {
    if (!Array.isArray(activityNotesDataTrendsMobile)) return [];
    if (!notesLabelFilter) return activityNotesDataTrendsMobile;
    return activityNotesDataTrendsMobile.filter(
      (note: any) =>
        Array.isArray(note?.labels) && note.labels.includes(notesLabelFilter),
    );
  }, [activityNotesDataTrendsMobile, notesLabelFilter]);

  const getNoteLeft = (index: number) => {
    const marginLeft = 10;
    const marginRight = 24;
    const dynamicWidth =
      chartData.length > 7 ? chartData.length * 60 : chartWidth;
    const usableWidth = dynamicWidth - marginLeft - marginRight;
    if (chartData.length <= 1) {
      return marginLeft + usableWidth / 2;
    }
    const step = usableWidth / (chartData.length - 1);
    return marginLeft + step * index;
  };

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const resize = new ResizeObserver((entries) => {
      setChartWidth(entries[0].contentRect.width);
    });
    resize.observe(chartRef.current);
    return () => resize.disconnect();
  }, []);

  const notesMap = React.useMemo(() => {
    if (!Array.isArray(filteredNotes)) return {};
    return filteredNotes.reduce((acc: any, note: any) => {
      if (!note?.activity_date) return acc;
      const formattedDate = format(new Date(note.activity_date), "dd MMM");
      if (!acc[formattedDate]) {
        acc[formattedDate] = [];
      }
      acc[formattedDate].push(note);
      return acc;
    }, {});
  }, [filteredNotes]);

  // const toggleFullNote = (index: number) => {
  //   setExpandedNotes((prev) => {
  //     const next = new Set(prev);
  //     next.has(index) ? next.delete(index) : next.add(index);
  //     return next;
  //   });
  // };

  const handleChartClick = (rechartsData: any) => {
    if (!rechartsData || rechartsData.activeTooltipIndex === undefined) {
      setActiveIndex(null);
      setActiveNote(null);
      return;
    }
    const index = rechartsData.activeTooltipIndex;
    const point = chartData[index];
    setActiveIndex((prev) => (prev === index ? null : index));
    if (point && notesMap[point.x]) {
      setActiveNote((prev) => (prev === index ? null : index));
    } else {
      setActiveNote(null);
    }
  };

  // const handleNoteClick = (index: number) => {
  //   setActiveNote((prev) => (prev === index ? null : index));
  // };

  const chartData = React.useMemo(() => {
    if (!Array.isArray(trendsData)) return [];
    const parsed = trendsData
      .map((item: any) => {
        if (!item?.time_unit) return null;
        const date = new Date(item.time_unit);
        const rawOutIn = item?.out_in;
        const rawRegsOut = item?.regs_out;
        const rawFtdRegs = item?.ftd_regs;
        const rawQftdFtd = item?.qftd_ftd;
        if (typeof rawOutIn !== "number" || isNaN(rawOutIn)) return null;
        if (typeof rawRegsOut !== "number" || isNaN(rawRegsOut)) return null;
        if (typeof rawFtdRegs !== "number" || isNaN(rawFtdRegs)) return null;
        if (typeof rawQftdFtd !== "number" || isNaN(rawQftdFtd)) return null;
        const out_in = parseFloat((rawOutIn * 100).toFixed(2));
        const regs_out = parseFloat((rawRegsOut * 100).toFixed(2));
        const ftd_regs = parseFloat((rawFtdRegs * 100).toFixed(2));
        const qftd_ftd = parseFloat((rawQftdFtd * 100).toFixed(2));
        if (isNaN(out_in)) return null;
        if (isNaN(regs_out)) return null;
        if (isNaN(ftd_regs)) return null;
        if (isNaN(qftd_ftd)) return null;
        return {
          x: format(date, "dd MMM"),
          rawDate: date,
          out_in,
          regs_out,
          ftd_regs,
          qftd_ftd,
        };
      })
      .filter(Boolean);
    parsed.sort((a: any, b: any) => a.rawDate.getTime() - b.rawDate.getTime());
    return parsed;
  }, [trendsData]);
  const yAxisConfig = React.useMemo(() => {
    return getMultiLineYAxisConfig(chartData, FUNNEL_RATIO_DATA_KEYS, [0, 100]);
  }, [chartData]);
  const notesIndices = React.useMemo(() => {
    return chartData
      .map((item: any, index) => (notesMap[item.x] ? index : null))
      .filter((i) => i !== null);
  }, [chartData, notesMap]);

  const notes = notesIndices;
  const activeNotes =
    activeNote !== null && chartData[activeNote]
      ? notesMap[chartData[activeNote].x]
      : null;

  const params = React.useMemo(() => {
    const baseParams = {
      oauthTokenHeader,
      queryId: isClickQuery
        ? TrendsDataSetQueryIds.CLICK
        : TrendsDataSetQueryIds.ACTION,
      startDate: startDate || "",
      endDate: endDate || "",
      timeUnit: timeUnit ? timeUnit : "day", // default
      type: "site",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions || [],
    };
    if (isClickQuery) {
      return {
        ...baseParams,
        trafficSource: filters.trafficSource,
        country: filters.country,
        campaignId: filters.campaign,
        adgroupId: filters.adGroup,
        kwId: filters.keyword,
        matchtype: "all",
      };
    }
    return baseParams;
    // eslint-disable-next-line
  }, [
    oauthTokenHeader,
    startDate,
    endDate,
    websiteSharedUids,
    selectedDimensions,
    filters,
  ]);

  const { fetchOverride } = useTrendsMobileOverride({
    params,
    setDataAction: setFrMobileDataRedux,
    setLoadingAction: setFrMobileLoading,
  });

  React.useEffect(() => {
    const timeUnit = searchParams.get("timeUnitFrMobile");
    if (!timeUnit) return;
    fetchOverride({
      ...params,
      timeUnit,
    });
    // eslint-disable-next-line
  }, [timeUnit]);

  if (loading) {
    return <OnlyMobgraphSkel />;
  }
  if (!chartData.length) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 242,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          color={colors.GrayLightMode[700].$value}
          sx={{
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            fontWeight: typography.Font_weight.Medium.$value,
          }}
        >
          No Data
        </Typography>
      </Box>
    );
  }

  return (
    <Box onClick={() => setActiveNote(null)}>
      <Box
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 3,
          backgroundColor: colors.Base.White.$value,
          px: 2.5,
          pt: 1,
          pb: 1.5,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 0.75,
        }}
      >
        {SERIES.map((series) => (
          <LegendChip
            key={series.id}
            series={series}
            activeSeries={activeSeries}
            onClick={(dataKey) =>
              setActiveSeries((prev) => (prev === dataKey ? null : dataKey))
            }
          />
        ))}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            width: 43,
            flexShrink: 0,
            position: "sticky",
            left: 0,
            zIndex: 2,
            backgroundColor: "#fff",
          }}
        >
          <ResponsiveContainer width="100%" height={265}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 0, left: 8, bottom: 31 }}
            >
              <XAxis
                dataKey="x"
                tick={false}
                tickLine={false}
                axisLine={false}
                height={48}
              />
              <YAxis
                domain={yAxisConfig.domain}
                ticks={yAxisConfig.ticks}
                tick={{
                  fill: colors.GrayLightMode[600].$value,
                  fontSize: typography.Size.Text.xxs.$value,
                  fontWeight: typography.Font_weight.Medium.$value,
                }}
                tickLine={false}
                axisLine={false}
                width={40}
                tickMargin={3}
                tickFormatter={(value) =>
                  Intl.NumberFormat(undefined, {
                    notation: "compact",
                    maximumFractionDigits: 1,
                  }).format(value)
                }
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        {/* Chart */}
        <Box sx={{ flex: 1, overflowX: "auto", overflowY: "hidden" }}>
          <Box
            ref={chartRef}
            sx={{
              minWidth:
                chartData.length > 7 ? `${chartData.length * 60}px` : "100%",
              position: "relative",
            }}
          >
            <ResponsiveContainer width="100%" height={265}>
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 24, left: 10, bottom: 48 }}
                onClick={(data, event) => {
                  event?.stopPropagation();
                  handleChartClick(data);
                }}
                style={{ cursor: "pointer" }}
              >
                <CartesianGrid
                  strokeDasharray=""
                  vertical={false}
                  stroke={colors.GrayLightMode[300].$value}
                  opacity={0.3}
                />
                <XAxis
                  dataKey="x"
                  tick={(tickProps: any) => (
                    <CustomXTick
                      {...tickProps}
                      activeNote={activeNote}
                      activeIndex={activeIndex}
                    />
                  )}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  tickMargin={10}
                />
                <YAxis
                  domain={yAxisConfig.domain}
                  ticks={yAxisConfig.ticks}
                  hide
                />
                <Tooltip
                  cursor={{
                    stroke: colors.GrayLightMode[200].$value,
                    strokeWidth: 1,
                  }}
                  content={(props) => (
                    <CustomTooltip {...props} activeSeries={activeSeries} />
                  )}
                />
                {SERIES.map((s) => (
                  <Line
                    key={s.id}
                    type="monotone"
                    dataKey={s.dataKey}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={
                      activeSeries === s.dataKey || activeSeries === null
                        ? 3
                        : 2
                    }
                    opacity={
                      activeSeries === null || activeSeries === s.dataKey
                        ? 1
                        : 0.2
                    }
                    dot={(dotProps: any) => (
                      <CustomDot
                        {...dotProps}
                        activeIndex={activeIndex}
                        color={s.color}
                        dataKey={s.dataKey}
                        onDotClick={(dataKey: string, index: number) => {
                          setActiveSeries(dataKey);
                          setActiveIndex(index);
                          if (notes.includes(index)) {
                            setActiveNote(index); // ← always OPEN, never toggle/close
                          } else {
                            setActiveNote(null);
                          }
                        }}
                      />
                    )}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            {/* Note Icons */}
            {notes.map((index) => (
              <IconButton
                key={index}
                size="small"
                sx={{
                  position: "absolute",
                  bottom: 4,
                  left: getNoteLeft(index),
                  transform: "translateX(-50%)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // handleNoteClick(index);
                }}
                disableRipple
              >
                {activeNote === index ? (
                  <StickyNote2OutlinedIcon
                    sx={{
                      width: "14px",
                      color: colors.GrayLightMode[900].$value,
                    }}
                  />
                ) : (
                  <StickyNote2OutlinedIcon
                    sx={{
                      width: "14px",
                      fill: colors.GrayLightMode[600].$value,
                    }}
                  />
                )}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Box>
      {/* Collapse note panel */}
      <Collapse in={activeNote !== null}>
        {activeNotes?.map((note: any, i: number) => (
          <Box
            key={i}
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              px: 1.5,
              py: 1,
              gap: 0.5,
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <Stack flexDirection="row" gap={0.5} alignItems="center">
              <Avatar sx={{ width: 18, height: 18 }} src="#" />
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                lineHeight={typography.Line_height.text.xs.$value}
                fontWeight={typography.Font_weight.Regular.$value}
                color={colors.GrayLightMode[700].$value}
              >
                {note.created_by}
              </Typography>
              <Chip
                label={note.note_type}
                variant="outlined"
                size="small"
                sx={{
                  height: spacing.Spacing["1xl"].$value,
                  fontSize: typography.Size.Text.xxs.$value,
                  lineHeight: typography.Line_height.text.xxs.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  color: colors.Brand[700].$value,
                  borderColor: colors.Brand[700].$value,
                  borderRadius: spacing.Radius["2xl"].$value,
                  ml: 0.5,
                }}
              />
            </Stack>
            <Box>
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                color={colors.GrayLightMode[900].$value}
                fontWeight={typography.Font_weight.Semibold.$value}
              >
                {note.note}
              </Typography>
              <Collapse in={expandedNotes.has(i)}>
                <Typography
                  fontSize={typography.Size.Text.xs.$value}
                  color={colors.GrayLightMode[900].$value}
                  fontWeight={typography.Font_weight.Semibold.$value}
                  mt={0.5}
                >
                  {note.details || "No additional details"}
                </Typography>
              </Collapse>
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                fontWeight={typography.Font_weight.Medium.$value}
                color={colors.Brand[700].$value}
                mt={0.5}
                sx={{
                  cursor: "not-allowed",
                  opacity: 0.5,
                  pointerEvents: "none",
                }}
                // onClick={() => toggleFullNote(i)}
              >
                {expandedNotes.has(i) ? "Hide full Note" : "See full Note"}
              </Typography>
            </Box>
          </Box>
        ))}
      </Collapse>
    </Box>
  );
}
